/**
 * Share Button Component
 * Handles sharing functionality using the Web Share API with fallback to clipboard copy
 */

class ShareButton extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('button');
    this.shareUrl = this.button?.dataset.shareUrl || window.location.href;
    this.shareTitle = this.button?.dataset.shareTitle || document.title;
    
    this.init();
  }

  init() {
    if (!this.button) return;

    this.button.addEventListener('click', () => this.handleShare());
    
    // Show/hide button based on share capability
    if (!this.canShare()) {
      this.button.classList.add('share-button--clipboard-only');
    }
  }

  /**
   * Check if the browser supports Web Share API
   */
  canShare() {
    return navigator.share !== undefined;
  }

  /**
   * Handle share button click
   */
  async handleShare() {
    const shareData = {
      title: this.shareTitle,
      url: this.shareUrl
    };

    try {
      if (this.canShare()) {
        await navigator.share(shareData);
        this.showFeedback('success', 'Shared successfully');
      } else {
        await this.copyToClipboard(this.shareUrl);
        this.showFeedback('success', 'Link copied to clipboard');
      }
    } catch (error) {
      // User cancelled or error occurred
      if (error.name !== 'AbortError') {
        console.error('Share failed:', error);
        // Try fallback to clipboard
        try {
          await this.copyToClipboard(this.shareUrl);
          this.showFeedback('success', 'Link copied to clipboard');
        } catch (clipboardError) {
          this.showFeedback('error', 'Failed to share');
        }
      }
    }
  }

  /**
   * Copy text to clipboard
   */
  async copyToClipboard(text) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
    } else {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      
      try {
        document.execCommand('copy');
      } finally {
        document.body.removeChild(textarea);
      }
    }
  }

  /**
   * Show feedback message
   */
  showFeedback(type, message) {
    const feedbackElement = document.createElement('div');
    feedbackElement.className = `share-button__feedback share-button__feedback--${type}`;
    feedbackElement.textContent = message;
    feedbackElement.setAttribute('role', 'status');
    feedbackElement.setAttribute('aria-live', 'polite');
    
    this.appendChild(feedbackElement);
    
    // Remove feedback after animation
    setTimeout(() => {
      feedbackElement.classList.add('share-button__feedback--hide');
      setTimeout(() => {
        feedbackElement.remove();
      }, 300);
    }, 2000);
  }
}

// Initialize all share buttons when DOM is ready
if (customElements.get('share-button') === undefined) {
  customElements.define('share-button', ShareButton);
}

